import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { bidNoBidApi, opportunitiesApi } from '../api/client';

// Seller lens: Bid / No-Bid analysis grounded in real RFQ->Award history.
// The numbers (award value, competitors, frequent winners) come from the backend
// SQL/Dynamo aggregation; the recommendation is advisory (AI or data fallback).

const money = (n?: number) =>
  n == null ? '—' : '$' + Math.round(n).toLocaleString();

const CALL_STYLE: Record<string, { bg: string; fg: string; label: string }> = {
  bid: { bg: '#dcfce7', fg: '#166534', label: 'BID' },
  no_bid: { bg: '#fee2e2', fg: '#991b1b', label: 'NO-BID' },
  investigate: { bg: '#fef9c3', fg: '#854d0e', label: 'INVESTIGATE' },
};

export default function BidNoBid() {
  const navigate = useNavigate();
  const [naics, setNaics] = useState('');
  const [agency, setAgency] = useState('');
  const [setAside, setSetAside] = useState('');
  const [title, setTitle] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<any>(null);
  const [adding, setAdding] = useState(false);

  // Turn the analyzed opportunity into a tracked seller pursuit (My Pursuits) and open it.
  const addToPursuits = async () => {
    setAdding(true); setError('');
    try {
      const res = await opportunitiesApi.create({
        pursuit_side: 'seller',
        title: title.trim() || `Pursuit — NAICS ${naics.trim()}`,
        agency: agency.trim(),
        naics_code: naics.trim(),
        set_aside: setAside.trim(),
        notes: result?.recommendation?.rationale
          ? `Bid/No-Bid: ${(result.recommendation.call || '').toUpperCase()} (${result.recommendation.confidence} confidence). ${result.recommendation.rationale}`
          : '',
      });
      const id = res?.opportunity_id || res?.id || (res?.item && res.item.id);
      if (id) navigate(`/opportunities/${id}`);
      else navigate('/opportunities');
    } catch (err: any) {
      setError(err?.message || 'Could not add to My Pursuits.');
      setAdding(false);
    }
  };

  const run = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!naics.trim()) { setError('NAICS code is required.'); return; }
    setError(''); setLoading(true); setResult(null);
    try {
      const res = await bidNoBidApi.analyze({
        naics_code: naics.trim(),
        agency: agency.trim() || undefined,
        set_aside_code: setAside.trim() || undefined,
        title: title.trim() || undefined,
      });
      setResult(res);
    } catch (err: any) {
      setError(err?.message || 'Analysis failed.');
    }
    setLoading(false);
  };

  const g = result?.grounding;
  const rec = result?.recommendation;
  const callStyle = rec ? (CALL_STYLE[rec.call] || CALL_STYLE.investigate) : null;

  return (
    <div style={{ maxWidth: 900 }}>
      <p style={{ color: '#475569', fontSize: 14, marginTop: 0 }}>
        Should we pursue this? Grounded in historical RFQ&rarr;Award data for the NAICS (and agency /
        set-aside when provided) &mdash; expected award value, who tends to win &mdash; and weighed
        against <strong>your saved capability profile</strong> for fit. Advisory Bid / No-Bid call.
      </p>

      <form onSubmit={run} style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end', marginBottom: 20 }}>
        <Field label="NAICS code *" value={naics} onChange={setNaics} placeholder="e.g. 541512" width={140} />
        <Field label="Agency" value={agency} onChange={setAgency} placeholder="e.g. DEPT OF DEFENSE" width={220} />
        <Field label="Set-aside code" value={setAside} onChange={setSetAside} placeholder="e.g. SBA" width={140} />
        <Field label="Title (optional)" value={title} onChange={setTitle} placeholder="Requirement title" width={220} />
        <button
          type="submit" disabled={loading}
          style={{ padding: '10px 20px', background: loading ? '#93c5fd' : '#4338ca', color: 'white',
                   border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: loading ? 'default' : 'pointer' }}
        >
          {loading ? 'Analyzing…' : 'Analyze'}
        </button>
      </form>

      {error && <div role="alert" style={{ color: '#991b1b', background: '#fee2e2', padding: '10px 14px', borderRadius: 8, marginBottom: 16 }}>{error}</div>}

      {result && g && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Recommendation banner */}
          {rec && callStyle && (
            <div style={{ border: '1px solid #e2e8f0', borderRadius: 12, padding: 18, background: 'white' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                <span style={{ background: callStyle.bg, color: callStyle.fg, fontWeight: 700, fontSize: 13,
                               padding: '4px 12px', borderRadius: 999 }}>{callStyle.label}</span>
                <span style={{ fontSize: 12, color: '#64748b' }}>confidence: {rec.confidence}</span>
                <span style={{ fontSize: 11, color: '#94a3b8', marginLeft: 'auto' }}>
                  {rec.source === 'ai' ? 'AI-assisted (advisory)' : 'data summary (advisory)'}
                </span>
              </div>
              <p style={{ margin: 0, color: '#1e293b', fontSize: 14, lineHeight: 1.5 }}>{rec.rationale}</p>
              {rec.capability_fit && (
                <p style={{ margin: '8px 0 0', fontSize: 13, color: '#0f766e' }}>
                  <strong>Fit for your capabilities:</strong> {rec.capability_fit}
                </p>
              )}
              <div style={{ marginTop: 12 }}>
                <button onClick={addToPursuits} disabled={adding}
                  style={{ padding: '8px 16px', background: adding ? '#94a3b8' : '#0f766e', color: 'white',
                           border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: adding ? 'default' : 'pointer' }}>
                  {adding ? 'Adding…' : '+ Add to My Pursuits'}
                </button>
                <span style={{ fontSize: 11, color: '#94a3b8', marginLeft: 10 }}>
                  Creates a tracked pursuit you can work (capture plan, compliance, proposal).
                </span>
              </div>
              {Array.isArray(rec.what_to_emphasize) && rec.what_to_emphasize.length > 0 && (
                <>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#334155', margin: '12px 0 4px' }}>What to emphasize</div>
                  <ul style={{ margin: 0, paddingLeft: 18, color: '#334155', fontSize: 13 }}>
                    {rec.what_to_emphasize.map((w: string, i: number) => <li key={i}>{w}</li>)}
                  </ul>
                </>
              )}
              {Array.isArray(rec.watch_outs) && rec.watch_outs.length > 0 && (
                <>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#334155', margin: '12px 0 4px' }}>Watch-outs</div>
                  <ul style={{ margin: 0, paddingLeft: 18, color: '#334155', fontSize: 13 }}>
                    {rec.watch_outs.map((w: string, i: number) => <li key={i}>{w}</li>)}
                  </ul>
                </>
              )}
            </div>
          )}

          {/* Grounding stats */}
          <div style={{ border: '1px solid #e2e8f0', borderRadius: 12, padding: 18, background: 'white' }}>
            <div style={{ fontSize: 12, color: '#64748b', marginBottom: 12 }}>
              Based on <strong>{g.comparable_count}</strong> comparable past awards (scope: {g.scope})
              {!g.has_enough_data && <span style={{ color: '#b45309' }}> — thin data, treat as indicative</span>}
            </div>
            {g.award_value ? (
              <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginBottom: 12 }}>
                <Stat label="Median award" value={money(g.award_value.median)} />
                <Stat label="Average" value={money(g.award_value.avg)} />
                <Stat label="Range" value={`${money(g.award_value.min)} – ${money(g.award_value.max)}`} />
                {g.competitor_signal && <Stat label="Typical competitors" value={String(g.competitor_signal.avg)} />}
              </div>
            ) : (
              <div style={{ color: '#64748b', fontSize: 13, marginBottom: 12 }}>No usable award amounts in comparable records.</div>
            )}
            {Array.isArray(g.frequent_winners) && g.frequent_winners.length > 0 && (
              <>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#334155', margin: '4px 0 6px' }}>Who tends to win here</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {g.frequent_winners.map((w: any, i: number) => (
                    <span key={i} style={{ fontSize: 12, background: '#f1f5f9', border: '1px solid #e2e8f0',
                                           borderRadius: 999, padding: '4px 10px', color: '#334155' }}>
                      {w.awardee} <strong>×{w.wins}</strong>
                    </span>
                  ))}
                </div>
              </>
            )}
          </div>

          <p style={{ fontSize: 11, color: '#94a3b8', margin: 0 }}>
            All figures are drawn from historical award records. Recommendations are advisory — the
            capture decision remains yours.
          </p>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, placeholder, width }:
  { label: string; value: string; onChange: (v: string) => void; placeholder?: string; width?: number }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: '#475569', fontWeight: 600 }}>
      {label}
      <input
        value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        style={{ width: width || 180, padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, fontWeight: 400 }}
      />
    </label>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 700, color: '#0f172a' }}>{value}</div>
    </div>
  );
}
